import PropTypes from 'prop-types';
import React, { useCallback, memo, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useWalletCalcConfig } from 'wallet-manager';
import { getConsumerMetadata, getAndSaveConsumerMetadata } from '@atc/consumer-metadata';
import { getFirstPartyVistorId, getPixallId } from '@atc/pixall-fns';
import { stringToInteger } from '@atc/string-fns';
import _isEmpty from 'lodash/isEmpty';
import { sendClick } from 'reaxl-analytics';
import { ownerEmailSent } from 'reaxl-analytics-handlers';
import { useFeatures } from '@atc/features';
import { getDeal } from 'wallet-deals';
import { useCookies } from 'react-cookie';
import { fallbackInterestRates } from 'axl-config';
import EmailOwner from '../EmailOwner';
import EmailStaticContent from '../EmailStaticContent';
import sendEmailAndSaveDeal from '../utilities/sendEmailAndSaveDeal';
/**
 *  Email is container for EmailOwner. It handles email submission and analytics logic
 *
 */
export const Email = /*#__PURE__*/memo(_ref => {
  let {
    buttonStyle,
    clickType,
    ctaProps = {},
    saveDealXgFetcher = () => {},
    fetcher = () => {},
    phoneClickHandler = () => {},
    formattedFilterValues = [],
    isLoggedIn = false,
    listing = {},
    owner = {},
    parentId = 'v_lstg',
    paymentsDuck,
    dealsDuck,
    userDuck,
    currentPageNameDuck,
    interestRateDuck,
    preorderInfo = {},
    preferenceSummary = {},
    sendClickEvent = () => {},
    uiContext = 'default',
    updateMyWallet = () => {},
    enableConsumerMetadata = false,
    activeInteractionDuck,
    oneClickOptIn,
    handleSaveChanges,
    handleEditSettingsClick,
    smsSubscribeFetcher,
    env,
    isBreakdownConfirmation,
    ...rest
  } = _ref;
  const {
    brand: [, {
      leads_cc_flag: copySelf,
      leads_partner_name: partnerName,
      consumer_brand: consumerBrand
    }],
    my_wallet: [, {
      enable_deals: enableDeals
    }],
    nds_calc: [ndsEnabled]
  } = useFeatures(['brand', 'my_wallet', 'nds_calc']);
  // Do NOT remove direct access to state for payments because paymentsDuck is not always passed through.
  const paymentsSelector = paymentsDuck ? paymentsDuck.selectors.getDuckState : state => state.payments;
  const payments = useSelector(paymentsSelector) || {};
  const dispatch = useDispatch();

  // TODO: Remove direct access to state for currentPageName and interestRates. Direct access was left for backwards compatibility.
  const currentPageNameSelector = currentPageNameDuck ? currentPageNameDuck.selectors.getDuckState : state => state.currentPageName;
  const currentPageName = useSelector(currentPageNameSelector) || '';
  const nationalRatesSelector = interestRateDuck ? interestRateDuck.selectors.getDuckState : state => state.interestRates;
  const nationalRates = useSelector(nationalRatesSelector) || fallbackInterestRates;
  const {
    context
  } = ctaProps;
  const isNDSCalc = context?.includes('calculator') || context === 'compareSendOffer'; // NDS Send Offer || Send Offer within compare modal
  const isQuote = context === 'quote';
  const isBuildAndPrice = uiContext === 'buildAndPrice';
  const isWallet = uiContext?.includes('wallet') || uiContext === 'wallet';
  const isBasic = uiContext === 'basic';
  const {
    esntial,
    id: listingId,
    pricingDetail = {},
    type: vehicleCondition = 'used',
    paymentServices = {},
    vin,
    selectedVrsData = {},
    trimSelected,
    ecommWebsite = {}
  } = listing;
  const {
    msrp,
    salePrice
  } = pricingDetail;
  const userZip = useSelector(state => userDuck?.selectors?.getZip(state));
  const profile = useSelector(state => userDuck?.selectors?.getUserProfile(state));
  const leadProfile = useSelector(state => userDuck?.selectors?.getLeadProfile(state));
  const userProfile = useSelector(state => userDuck?.selectors?.getSelectedUserProfile(state)) || {};
  const [cookies = {}] = useCookies(['ATC_ID', 'pxa_ipv4']);
  const preorderContent = isQuote && preorderInfo || isBuildAndPrice && preferenceSummary;
  const {
    preferredLenderSpecified,
    digitalRetailingType
  } = paymentServices;
  const isAccelerate = digitalRetailingType === 'accelerate';
  const isEsntial = esntial && !esntial?.deemphasized && !!Object.keys(ecommWebsite).length;
  const submitButtonStyle = owner.dealerPreferredCTA?.toLowerCase() === 'website' || isEsntial ? 'secondary' : 'primary';
  const {
    cashDealPrice,
    disableTrade
  } = useWalletCalcConfig(payments, listing);

  // analytics related and set uiContext
  let vdpType = '';
  if (isNDSCalc) {
    // set uiContext to be calc when NDS calculator Dealer CTA is clicked
    uiContext = context !== 'compareSendOffer' && context !== 'calculator' ? 'calculatorOfferSummary' : 'calculator';
    if (isBreakdownConfirmation) {
      uiContext = 'calculatorBreakdownConfirmation';
    }
    if (context === 'compareSendOffer') {
      // Send Offer CTA within compare modal is clicked
      vdpType = 'Compare';
    } else if (isAccelerate) {
      vdpType = 'dr3';
    } else {
      vdpType = 'MyWallet';
    }
  } else if (isQuote) {
    uiContext = 'quote';
  } else if (context === 'mediaGallery') {
    vdpType = 'MediaGalleryCTA';
  }
  const originPage = currentPageName + vdpType;

  // nds and wallet related
  const [dealToSubmit, setDealToSubmit] = useState({});
  const [walletSummary, setWalletSummary] = useState([]);
  const [walletEstimates, setWalletEstimates] = useState({});
  const [oneClickChecked, setOneClickChecked] = useState(oneClickOptIn || leadProfile?.isOneClickOptedIn);
  const [messageOptIn, setMessageOptIn] = useState(false);
  const dealXgIdRef = useRef();
  const {
    downPayment,
    downPaymentPercent,
    interestRate,
    loanTerm,
    interactions: walletInteractions = {},
    dealType,
    creditAppDeals: creditApplications = [],
    walletId
  } = payments;
  const {
    paymentEmphasis,
    hasUpdatedWallet,
    hasUpdatedNDSCalc,
    useFallbackLead
  } = walletInteractions;
  const isUserInvolved = paymentEmphasis || hasUpdatedWallet || hasUpdatedNDSCalc;
  const walletOptIn = ndsEnabled && isUserInvolved && !isNDSCalc && !isWallet && !isBasic;
  const existingDeal = useSelector(state => dealsDuck?.selectors?.getDealByListingVin(state, vin)) || {};
  dealXgIdRef.current = existingDeal?.id;
  const hasPrice = !!(salePrice || msrp);
  const enableSaveDeal = hasPrice && enableDeals && ndsEnabled && isUserInvolved && !isWallet;
  const setDealInfo = useCallback(() => {
    const {
      deal = {},
      summary = [],
      estimates = {}
    } = getDeal(listing, payments, nationalRates, isNDSCalc, cashDealPrice, disableTrade);
    setDealToSubmit(deal);
    setWalletSummary(summary);
    setWalletEstimates(estimates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNDSCalc,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  JSON.stringify(payments),
  // Use deep object comparison not referential equality
  // eslint-disable-next-line react-hooks/exhaustive-deps
  JSON.stringify(pricingDetail),
  // Use deep object comparison not referential equality
  vehicleCondition]);
  useEffect(() => {
    // get deal info for WalletOptIn
    if (walletOptIn || isNDSCalc || isWallet) {
      setDealInfo();
    }
  }, [isNDSCalc, isWallet, setDealInfo, walletOptIn]);
  useEffect(() => {
    // get ATC_USER.leadProfile from localStorage and dispatch it to userDuck
    const {
      firstName,
      lastName
    } = profile ?? {};
    if (userDuck && (!firstName || !lastName || !isLoggedIn) && !leadProfile) {
      dispatch(userDuck.creators.setLeadProfile());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    // set oneClickChecked default when rendering the emailForm
    setOneClickChecked(oneClickOptIn || leadProfile?.isOneClickOptedIn);
  }, [oneClickOptIn, leadProfile?.isOneClickOptedIn]);
  const handleOneClickEmailSaveChanges = useCallback((checked, formData) => {
    const {
      firstName,
      lastName,
      fromEmailAddress,
      phoneNumber,
      isOneClickOptedIn
    } = formData || {};
    dispatch(activeInteractionDuck.creators.setKeys({
      isOneClickOptedIn: checked
    }));
    dispatch(userDuck.creators.setLeadProfile({
      firstName,
      lastName,
      email: fromEmailAddress,
      phone: phoneNumber,
      isOneClickOptedIn
    }, true));
    sendClick('oneClickButtonClick', {}, {
      listingId,
      clickType,
      element: 'cta',
      content: 'save-changes'
    });
  }, [activeInteractionDuck, clickType, dispatch, listingId, userDuck]);
  const handleOneClickEmailSend = useCallback(async event => {
    const {
      email,
      firstName,
      lastName
    } = leadProfile || {};
    if (!email || !firstName || !lastName) {
      handleEditSettingsClick();
      return null;
    }
    sendClick(ownerEmailSent, event, {
      inventoryId: listingId,
      hasListingType: true,
      includeWalletData: false,
      isOneClick: true,
      par: parentId
    });
    const {
      responseDealXgId,
      emailResult
    } = await sendEmailAndSaveDeal({
      clickType,
      cookies,
      copySelf,
      deal: dealToSubmit,
      dealXgIdRef,
      dealsDuck,
      disableTrade,
      dispatch,
      emailOwnerFetcher: fetcher,
      enableSaveDeal,
      env,
      formData: {
        comments: 'Please confirm vehicle availability.',
        firstName: leadProfile.firstName,
        fromEmailAddress: leadProfile?.email,
        lastName: leadProfile?.lastName,
        phoneNumber: leadProfile?.phone
      },
      isAccelerate,
      listing,
      oneClick: true,
      originPage: 'vdp',
      owner,
      partnerName,
      paymentsDuck,
      pixallFirstPartyId: getFirstPartyVistorId(),
      pixallThirdPartyId: getPixallId() || 'nl',
      preorderContent,
      saveDealXgFetcher,
      userZip,
      vdpType,
      walletId
    });
    if (emailResult?.email?.status === 'SUCCESS') {
      dealXgIdRef.current = responseDealXgId;
    }
    return emailResult;
  }, [leadProfile, clickType, cookies, copySelf, listing, partnerName, userZip, listingId, parentId, fetcher, handleEditSettingsClick, dealToSubmit, saveDealXgFetcher, dealsDuck, enableSaveDeal, isAccelerate, paymentsDuck, walletId, dispatch, disableTrade, owner, preorderContent, vdpType, env]);
  function catchEmailError(data, response) {
    if (response?.errorMessage) {
      if (window && window.newrelic) {
        window.newrelic.addPageAction('LeadsSubmitAction', {
          id: data.listingId,
          component: 'ConnectedEmailOwner',
          message: response.logMessage
        });
      }
      return response.errorMessage;
    }
    return null;
  }
  const getAdditionalInventoryDeal = useCallback((selectedListing, dealData) => {
    let selectedListingCondition = selectedListing?.selectedListingType?.toLowerCase() ?? 'used';
    if (selectedListingCondition === 'certified') selectedListingCondition = 'used';
    const selectedListingRate = interestRate?.[selectedListingCondition].toString() ?? null;
    let selectedListingDownPayment = downPayment;
    if (downPayment === null) {
      selectedListingDownPayment = Math.floor(selectedListing.pricingDetail.salePrice * downPaymentPercent);
    }
    const updatedDeal = {
      ...dealData,
      dealType: 'finance',
      terms: loanTerm,
      rate: selectedListingRate,
      downPayment: selectedListingDownPayment
    };

    // remove vehicle data from deal for additional inventory
    delete updatedDeal.amountFinanced;
    delete updatedDeal.customPrice;
    delete updatedDeal.lender;
    delete updatedDeal.payment;
    delete updatedDeal.salePrice;
    delete updatedDeal.instantCashOffer;
    return updatedDeal;
  }, [downPayment, downPaymentPercent, interestRate, loanTerm]);
  const onEmailSubmit = useCallback(async (formData, inquirySelection, selectedListings) => {
    // validate user selected all required preferences when submit email in Build & Price page
    if (isBuildAndPrice) {
      // check if the preference are not Package and is not selected
      const hasUnselectedPreference = preferenceSummary.preferenceSelections?.some(_ref2 => {
        let {
          name,
          selectedOptions
        } = _ref2;
        return !['packageList'].includes(name) && !selectedOptions[0]?.label;
      });
      if (hasUnselectedPreference) {
        return 'bapNotSelectedError';
      }
    }
    const pixallFirstPartyId = getFirstPartyVistorId();
    const pixallThirdPartyId = getPixallId() || 'nl';
    const {
      inquiry: inquiryConfig
    } = EmailStaticContent;
    const serviceInquiry = !!inquiryConfig[inquirySelection]?.value && {
      serviceInquiry: inquiryConfig[inquirySelection]?.value
    };
    let dealData = '';
    const includeWalletData = isNDSCalc || formData.includeWalletData || isWallet;
    if (includeWalletData) {
      dealData = dealToSubmit;
    }
    const saveIcoData = isEsntial && payments?.tradeIn?.ico?.length;

    // get consumer metadata from formData or from ClientAuth (if user is logged in)
    let consumerMetadata = {
      email: formData.fromEmailAddress,
      phoneNumber: formData?.phoneNumber?.toString()
    };
    try {
      const metaData = await getConsumerMetadata();
      if (!_isEmpty(metaData)) {
        consumerMetadata = metaData;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to get consumer metadata');
    }
    if (enableConsumerMetadata) {
      await getAndSaveConsumerMetadata({
        email: formData.fromEmailAddress,
        brand: consumerBrand,
        shouldUpdateConsumerMetadata: true
      });
    }
    let serverErrors = [];

    // Email Data that is reused between default lead and additional inventory
    const defaultEmailData = {
      cookies,
      copySelf,
      disableTrade,
      formData,
      includeWalletData,
      partnerName,
      pixallFirstPartyId,
      pixallThirdPartyId,
      userZip,
      vdpType
    };
    if (!selectedListings.length) {
      // First Email Submission

      const {
        emailData,
        emailResult,
        responseDealXgId
      } = await sendEmailAndSaveDeal({
        ...defaultEmailData,
        deal: dealData,
        clickType,
        consumerBrand,
        dealXgIdRef,
        dealsDuck,
        dispatch,
        emailOwnerFetcher: fetcher,
        enableSaveDeal,
        env,
        formData,
        icoTradeIn: saveIcoData ? payments?.tradeIn?.ico?.[0] : undefined,
        isAccelerate,
        isScheduleTestDrive: inquirySelection === 'scheduleTestDrive',
        isBuildAndPrice,
        isQuote,
        isWallet,
        listing,
        originPage,
        owner,
        paymentsDuck,
        preorderContent,
        serviceInquiry,
        saveDealXgFetcher,
        walletId
      });
      dealXgIdRef.current = responseDealXgId;
      serverErrors = [catchEmailError(emailData, emailResult)];

      // Fires Email Tag
      sendClick(ownerEmailSent, null, {
        ...emailData,
        consumerMetadata,
        deal: dealToSubmit,
        inventoryId: listingId,
        par: parentId,
        isChipEnabled: true,
        serviceInquiry: inquirySelection,
        inquiryLabel: inquiryConfig[inquirySelection]?.label,
        context: context || uiContext,
        selectedVrsData,
        trimSelected,
        includePageId: !['incentives'].includes(context)
      });
      if (uiContext === 'default' && messageOptIn && typeof smsSubscribeFetcher === 'function') {
        const smsSubscribeData = {
          brand: consumerBrand,
          email: formData?.fromEmailAddress,
          firstName: formData?.firstName,
          lastName: formData?.lastName,
          destinationNumber: formData?.phoneNumber?.replace(/\D/g, ''),
          title: listing?.listingTitle,
          listingId: listing?.id,
          makeCode: listing?.makeCode,
          modelCode: listing?.modelCode,
          mileage: listing?.mileage?.value,
          year: listing?.year,
          listingType: listing?.listingType,
          listingZip: listing?.zip,
          userZip
        };
        smsSubscribeFetcher(smsSubscribeData);
      }
    } else {
      // Post Lead Email Submission
      selectedListings.forEach(selectedListing => {
        const updatedDeal = getAdditionalInventoryDeal(selectedListing, dealData);
        const postLeadData = {
          ...defaultEmailData,
          deal: updatedDeal,
          emailOwnerFetcher: fetcher,
          clickType: null,
          listing: {
            id: selectedListing.id,
            listingTitle: selectedListing.title
          },
          originPage: 'emailConfirmation',
          owner: selectedListing?.owner
        };
        const {
          emailData,
          emailResult
        } = sendEmailAndSaveDeal(postLeadData);
        const serverError = catchEmailError(emailData, emailResult);
        if (serverError) {
          serverErrors.push(serverError);
        }

        // fire tracking for submission on recently viewed vehicle
        sendClick(ownerEmailSent, null, {
          ...defaultEmailData,
          consumerMetadata,
          deal: dealToSubmit,
          inventoryId: selectedListing.id,
          par: parentId,
          // SelectedListings/additionalInventory/recentlyViewed is associated with emailConfirmationModal/NBA context
          context: 'emailConfirmation',
          includePageId: false,
          ownerId: selectedListing?.owner?.id
        });
      });
    }

    // Store input values in localStorage and userDuck

    const userInputs = {
      firstName: defaultEmailData?.formData?.firstName,
      lastName: defaultEmailData?.formData?.lastName,
      email: defaultEmailData?.formData?.fromEmailAddress,
      phone: formData.phoneNumber,
      isOneClickOptedIn: formData?.isOneClickOptedIn || leadProfile?.isOneClickOptedIn
    };
    if (userDuck) {
      dispatch(userDuck.creators.setLeadProfile(userInputs, true));
    }
    return serverErrors[0];
  }, [fetcher, payments?.tradeIn?.ico, isEsntial, isBuildAndPrice, isNDSCalc, isWallet, clickType, cookies, copySelf, disableTrade, isQuote, listing, messageOptIn, originPage, owner, partnerName, preorderContent, userZip, vdpType, enableConsumerMetadata, leadProfile?.isOneClickOptedIn, userDuck, preferenceSummary.preferenceSelections, dealToSubmit, consumerBrand, parentId, listingId, context, uiContext, selectedVrsData, trimSelected, dispatch, smsSubscribeFetcher, saveDealXgFetcher, dealsDuck, enableSaveDeal, isAccelerate, paymentsDuck, walletId, env, getAdditionalInventoryDeal]);
  return /*#__PURE__*/React.createElement(EmailOwner, Object.assign({
    buttonStyle: buttonStyle || submitButtonStyle,
    ctaProps: ctaProps,
    messageOptIn: messageOptIn,
    setMessageOptIn: setMessageOptIn,
    formattedFilterValues: formattedFilterValues,
    listing: listing,
    userDuck: userDuck,
    ndsProps: {
      dealType,
      walletOptIn,
      updateMyWallet,
      walletSummary,
      walletEstimates,
      preferredLenderSpecified,
      useFallbackLead,
      creditApplications,
      payments: {
        ...dealToSubmit,
        creditType: dealToSubmit.creditRange,
        loanTerm: dealToSubmit.terms,
        downPayment: stringToInteger(dealToSubmit?.downPayment ?? ''),
        salePrice: stringToInteger(dealToSubmit?.salePrice ?? ''),
        tradeIn: payments?.tradeIn,
        interactions: walletInteractions
      }
    },
    setDealInfo: setDealInfo,
    oneClickChecked: oneClickChecked,
    handleSaveChanges: handleSaveChanges || handleOneClickEmailSaveChanges,
    handleEditSettingsClick: handleEditSettingsClick,
    handleOneClickEmailSend: handleOneClickEmailSend,
    setOneClickChecked: setOneClickChecked,
    onEmailSubmit: onEmailSubmit,
    owner: owner,
    phoneClickHandler: phoneClickHandler,
    uiContext: uiContext,
    userProfile: userProfile,
    clickType: clickType,
    isLoggedIn: isLoggedIn
  }, rest));
});
export default Email;